import React, { useCallback } from 'react';
import type { FunctionComponent } from 'react';
import debouncePromise from 'debounce-promise';
import { useIntl } from 'react-intl-next';
import type { AriaOnFocusProps, GroupBase } from '@atlaskit/react-select';

import Avatar from '@atlaskit/avatar';
import { ColumnFilterGroup, RowFilterGroup } from '@atlassian/search-dialog/filter-groups';
import {
	FilterColLabelText,
	FilterRowLabelText,
} from '@atlassian/search-dialog/filter-item.styled';
import { FilterShowMore, type OptionType } from '@atlassian/search-dialog/filter-show-more';
import { OptionVariant } from '@atlassian/search-dialog/filter-item';
import { AsyncSelectFilterComponent } from '@atlassian/search-dialog/async-select-component';

import { FilterOptionSource } from '../../../../../../common/filters/types';
import { useAnalytics } from '../../../../../../common/analytics/use-analytics';
import { onFilterSelect } from '../../../../../../common/analytics/events';
import { ProductFilterItem } from '../../../../../../common/product-search-filter/product-filter-item';
import { messages } from '../../../../../../messages';

import type {
	FilterConfig,
	GenericFilterOption,
	FilterStore,
	FilterLoader,
} from '../../../../../filters/filter-types';
import { FilterOptionItem } from './filter-option-item';

const SHOW_MORE_DEBOUNCE_TIME = 250;

export interface CheckboxListFilterProps {
	filterType: string;
	store: FilterStore<GenericFilterOption>;
	loadFilterOptions: FilterLoader;
	sectionLabel: string;
	findMoreLabel: string;
	maxSelectable?: number;
	filterConfig?: FilterConfig;
}

export const CheckboxListFilter: FunctionComponent<CheckboxListFilterProps> = ({
	filterType,
	store,
	loadFilterOptions,
	sectionLabel,
	findMoreLabel,
	maxSelectable,
	filterConfig,
}) => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { availableFilters, updateFilter, addFilters, isLoading } = store;

	const usePreloadedOptions = filterConfig?.usePreloadedOptions || false;
	const showIcons = !filterConfig?.hideIcon;
	const appearanceIcon = filterConfig?.filterOptionIconShape || 'square';

	const options = availableFilters.map((filterOption) => ({
		label: (
			<FilterOptionItem
				label={filterOption.label}
				iconProps={{
					src: filterOption.avatarUrl,
					appearance: filterConfig?.filterOptionIconShape,
				}}
			/>
		),
		value: filterOption,
	}));

	const visibleOptions = options.filter((option) => option.value.isVisible);

	const onChangeCallback = useCallback(
		(value: any, _: any, checked: boolean) => {
			updateFilter(value, checked);
		},
		[updateFilter],
	);

	const addFilter = useCallback(
		({ value }: { value: any }) => {
			addFilters([value]);
			updateFilter(value.id, true);
			fireAnalyticsEvent(
				onFilterSelect({
					filterId: value.id,
					filterSource: FilterOptionSource.EXTERNAL,
					filterType: filterType,
				}),
			);
		},
		[addFilters, updateFilter, fireAnalyticsEvent, filterType],
	);

	const loadMoreFilters = debouncePromise(
		(query: string) => loadFilterOptions(query),
		SHOW_MORE_DEBOUNCE_TIME,
	);

	const loadAvatar = useCallback(
		({ avatarUrl: iconUrl, name }: any) => {
			if (showIcons) {
				return (
					<Avatar
						borderColor="transparent"
						src={iconUrl}
						appearance={appearanceIcon}
						size={'small'}
						name={name}
					/>
				);
			}
		},
		[showIcons, appearanceIcon],
	);

	const checkedValues = availableFilters.filter((filter) => filter.isChecked);

	const showMoreOptions = options.filter((option) => !option.value.isVisible);

	const filterPreloadedOptions = (query: string) =>
		Promise.resolve(
			showMoreOptions.filter(
				(option) => option.value.label.indexOf(query.trim().toLowerCase()) !== -1,
			),
		);

	const isMaxValuesSelected = !!maxSelectable && checkedValues.length === maxSelectable;

	const loadFilterItems = (isColumnGrouped: boolean = false) =>
		visibleOptions.map(({ value }) => (
			<ProductFilterItem
				value={value.id}
				filterType={filterType}
				icon={loadAvatar(value)}
				label={value.label}
				onChange={onChangeCallback}
				isChecked={value.isChecked}
				filterSource={value.filterSource}
				isDisabled={isMaxValuesSelected && !value.isChecked}
				variant={OptionVariant.CHECKBOX}
				LabelComponent={
					filterConfig?.labelComponent ??
					(isColumnGrouped ? FilterColLabelText : FilterRowLabelText)
				}
			/>
		));

	const onFocusAriaMessage = useCallback(
		(
			props: AriaOnFocusProps<
				OptionType<GenericFilterOption>,
				GroupBase<OptionType<GenericFilterOption>>
			>,
		) => {
			const { focused, options } = props;
			const optionIndex = options?.findIndex((option) => {
				return option === focused;
			});

			return `Option ${focused.value.label} focused, ${optionIndex + 1} out of ${options.length}.`;
		},
		[],
	) as (props: AriaOnFocusProps<unknown, GroupBase<unknown>>) => string;

	const loadFilterShowMore = () =>
		!(usePreloadedOptions && showMoreOptions.length === 0) && (
			<FilterShowMore<GenericFilterOption>
				placeholderText={findMoreLabel}
				addFilter={addFilter}
				defaultOptions={showMoreOptions || []}
				loadOptions={usePreloadedOptions ? filterPreloadedOptions : loadMoreFilters}
				buttonLabel={findMoreLabel}
				buttonText={formatMessage(messages.show_more_filters)}
				filterComponent={AsyncSelectFilterComponent}
				loadingMessage={() => formatMessage(messages.loading_filters)}
				noOptionsMessage={() => formatMessage(messages.no_options_filters)}
				isDisabled={isMaxValuesSelected}
				onFocusAriaMessage={onFocusAriaMessage}
			/>
		);

	return (
		<>
			{(!filterConfig?.filterGroupedBy || filterConfig?.filterGroupedBy === 'column') && (
				<ColumnFilterGroup
					title={sectionLabel}
					isLoading={isLoading}
					avatarShape={filterConfig?.filterOptionIconShape ?? 'circle'}
				>
					{loadFilterItems(true)}
					{loadFilterShowMore()}
				</ColumnFilterGroup>
			)}
			{filterConfig?.filterGroupedBy === 'row' && (
				<RowFilterGroup title={sectionLabel} isLoading={false}>
					{loadFilterItems(false)}
					{loadFilterShowMore()}
				</RowFilterGroup>
			)}
		</>
	);
};
